import { Box, FormControlLabel, Paper, styled, Switch, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { Permissions, Project, ProjectStatuses } from "../api/dtos";
import GridActions from "../components/GridActions";
import SetLinks from "../components/layout/SetLinks";
import { StyledButton } from "../components/StyledButton";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import { StyledLink } from "../components/utility/StyledLink";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import ProjectsContext from "../contexts/data/ProjectsContext";
import { DefaultProjectCreateLink } from "../contexts/layouts/LinkContext";
import ProjectSummariesContext from "../contexts/ProjectSummariesContext";
import useProjectSummaries from "../hooks/data/useLotStatusCountsByProjectId";
import { GRID_AGGREGATION_ROOT_FOOTER_ROW_ID, GridAggregationModel } from "@mui/x-data-grid-premium";

const StyledPaper = styled(Paper) (({theme}) => ({
    marginBottom: theme.spacing(2)
}));

const StyledBox = styled(Box) (({theme}) => ({
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2)
}));

const useProjectColumns = () => {
    const {data: permissions} = useContext(PermissionsContext);

    const projectColumns = useMemo(() => {
        const columns : GridColDef<Project>[] = [
            { 
                field: "name",
                headerName: "Project Name",
                minWidth: 125,
                flex: 1,
                renderCell: (params) => (
                    <StyledLink
                        to={`/projects/${params.row.id}`}
                    >
                        {params.formattedValue}
                    </StyledLink>
                )
            },
            { 
                field: "future",
                headerName: "Future Lots",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 110
            },
            { 
                field: "unavailable",
                headerName: "Unavailable",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 125
            },
            { 
                field: "comingSoon",
                headerName: "Coming Soon",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 125
            },
            { 
                field: "onHold",
                headerName: "On Hold",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 125
            },
            { 
                field: "available",
                headerName: "Available",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 125
            },
            { 
                field: "salesAdviceIssued",
                headerName: "Sales Advice Issued",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 160
            },
            {
                field: "contractIssued",
                headerName: "Contract Issued",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 125
            },
            {
                field: "contractExchanged",
                headerName: "Contract Exchanged",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 150
            },
            {
                field: "contractSettled",
                headerName: "Contract Settled",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 125
            },
            {
                field: "totalLots",
                headerName: "Total Lots",
                headerAlign: "left",
                align: "left",
                type: "number",
                width: 125
            },
        ]

        if(permissions?.includes(Permissions.ProjectsCanEditProject)) {
            columns.push({
                field: "actions",
                headerName: '',
                width: 90,
                renderCell: (params) => {
                    return (
                        params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID ? 
                        null :
                        <GridActions 
                            editPermission={Permissions.ProjectsCanEditProject}
                            editUrl={`/projects/edit/${params.row.id}`}
                        />
                    )
                },
                filterable: false,
                sortable: false,
                disableExport: true,
                aggregable: false
            })
        }

        const aggregationModel: GridAggregationModel = {
            future: 'sum',
            unavailable: 'sum',
            comingSoon: 'sum',
            onHold: 'sum',
            available: 'sum',
            salesAdviceIssued: 'sum',
            contractIssued: 'sum',
            contractExchanged: 'sum',
            contractSettled: 'sum',
            totalLots: 'sum'
        }

        return { columns, aggregationModel };
    }, [permissions]);

    return projectColumns;
}


export const Projects : FC = () => {
    const {data: permissions} = useContext(PermissionsContext);

    const navigate = useNavigate();

    const {data: projects} = useContext(ProjectsContext);

    const theme = useTheme();

    const [showArchived, setShowArchived] = useState<boolean>(false);

    useEffect(() => {
        if(permissions?.includes(Permissions.ProjectsCanSeeArchivedStages)) {
            setShowArchived(true);
        }
    }, [permissions])

    const context = useProjectSummaries(showArchived);
    const {data: projectSummaries, loading: projectsLoading} = context;

    const releasedToMarket = useMemo(() => {
        return projectSummaries?.filter(x => x.status === ProjectStatuses.ReleasedToMarket);
    }, [projectSummaries]);

    const pipeline = useMemo(() => {
        return projectSummaries?.filter(x => x.status === ProjectStatuses.Pipeline);
    }, [projectSummaries]);

    const archived = useMemo(() => {
        return projectSummaries?.filter(x => x.status === ProjectStatuses.Archived);
    }, [projectSummaries]);

    if(!permissions?.includes(Permissions.ProjectsCanViewPage)) {
        if(projects && projects.length > 0) {
            navigate(`projects/${projects[0]?.id}`);
        } else {
            navigate('error')
        }
    }

    const {columns: projectCols, aggregationModel: aggregationModel} = useProjectColumns();
    
    const pipelineCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `Pipeline Projects`,
        fileName: `Pipeline Projects`,
        includeDateTime: true
    }), []);
    
    const releasedToMarketCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `Released To Market Projects`,
        fileName: `Released To Market Projects`,
        includeDateTime: true
    }), []);
    
    const archivedCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `Archived Projects`,
        fileName: `Archived Projects`,
        includeDateTime: true
    }), []);

    const toggleArchived = () => {
        setShowArchived(state => !state);
    }
    
    return (
        <ProjectSummariesContext value={context}>
            <Outlet />

            <SetLinks 
                createProjectLink={DefaultProjectCreateLink} 
                stageLinkEnabled={false} 
                lotLinkEnabled={false} 
            />
            
            <StyledBox>
                {
                    permissions?.includes(Permissions.ProjectsCanSeeArchivedStages)
                    ?
                    <FormControlLabel
                        label="Show Archived Stages"
                        control={
                            <Switch 
                                checked={showArchived}
                                onChange={toggleArchived}
                            />
                        }
                    />
                    : ""
                }
                {
                    permissions?.includes(Permissions.ProjectsCanAddProject) && 
                    <StyledButton 
                        variant="contained"
                        //@ts-expect-error
                        component={Link}
                        to="/projects/create"
                        state={{
                            back: true
                        }}
                    >
                        Add Project
                    </StyledButton>
                }
            </StyledBox>
            <StyledPaper>
                <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
    
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                            
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                            
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                        
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                }
                    
                            }
                        },
                    }}
                >
                    <CollapsibleTablePremium 
                        density="compact"
                        title="Released To Market"
                        pluralizeTitle={false}
                        entityName="Projects"
                        loading={projectsLoading} 
                        columns={projectCols}
                        rows={releasedToMarket}
                        disableColumnFilter={false}
                        disableColumnMenu={false}
                        aggregationModel={aggregationModel}
                        {...releasedToMarketCustomToolbarProps}
                    />
                </Box>
            </StyledPaper>

            <StyledPaper>
            <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
    
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                            
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                            
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                        
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                }
                    
                            }
                        },
                    }}
                >
                    <CollapsibleTablePremium 
                        density="compact"
                        title="Pipeline"
                        pluralizeTitle={false}
                        startCollapsed
                        entityName="Projects"
                        loading={projectsLoading} 
                        columns={projectCols}
                        rows={pipeline}
                        aggregationModel={aggregationModel}
                        disableColumnFilter={false}
                        disableColumnMenu={false}
                        {...pipelineCustomToolbarProps}
                    />
                </Box>
            </StyledPaper>

            <StyledPaper>
            <Box
                    sx={{
                        [`& .${gridClasses.root}`]: {
    
                            [`& .${gridClasses.columnHeader}`]: {
                                height: 'auto !important',
                                minHeight: 56,
                            
                                [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                    whiteSpace: 'normal',
                            
                                    [`& .${gridClasses.columnHeaderTitle}`]: {
                                        whiteSpace: 'normal',
                                        paddingTop: theme.spacing(1.5),
                                        paddingBottom: theme.spacing(1.5) 
                                    }
                                },
                        
                                [`& .${gridClasses.iconButtonContainer}`]: {
                                    width: 'unset',
                                }
                    
                            }
                        },
                    }}
                >
                    <CollapsibleTablePremium
                        density="compact"
                        title="Archived"
                        pluralizeTitle={false}
                        startCollapsed
                        entityName="Projects"
                        loading={projectsLoading} 
                        columns={projectCols}
                        rows={archived}
                        aggregationModel={aggregationModel}
                        disableColumnFilter={false}
                        disableColumnMenu={false}
                        {...archivedCustomToolbarProps}
                    />
                </Box>
            </StyledPaper>
        </ProjectSummariesContext>
    );
}